import * as echarts from 'echarts'
import { useEffect, useRef } from 'react'

import { resolveIsDark, useThemeStore } from '@/stores/theme-store'

export interface EChartProps {
  /** ECharts seçeneği; eksen/seri anlamı çağırandan gelir (bu bileşen iş alanı bilmez). */
  option: echarts.EChartsOption
  className?: string
  height?: number | string
  /** Yükleniyor/boş/hata durumları çağıran tarafından yönetilir; bu yalnızca grafik alanı. */
  notMerge?: boolean
  /** Veri öğesine tık (çapraz filtre). Yeni işlev verilince eski dinleyici kaldırılır. */
  onClick?: (params: echarts.ECElementEvent) => void
  /** Baskı: sabit genişlik (sayı px); verilmezse kapsayıcıyı doldurur. */
  width?: number
  /** Baskı: `svg` çizer (vektör, sayfa kırığında net). */
  renderer?: 'canvas' | 'svg'
  /** Uygulama teması ne olursa olsun açık temayı kullanır (baskı kâğıda açık basar). */
  light?: boolean
}

const LIGHT_THEME = 'betontasdash-light'
const DARK_THEME = 'betontasdash-dark'
let themesRegistered = false

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/** Tema değişkenlerinden (bkz. `index.css`) türetilmiş iki ECharts teması, bir kez kaydedilir. */
function registerThemesOnce(): void {
  if (themesRegistered) return
  themesRegistered = true

  for (const name of [LIGHT_THEME, DARK_THEME]) {
    const wasDark = document.documentElement.classList.contains('dark')
    document.documentElement.classList.toggle('dark', name === DARK_THEME)
    const palette = [
      cssVar('--chart-1'),
      cssVar('--chart-2'),
      cssVar('--chart-3'),
      cssVar('--chart-4'),
      cssVar('--chart-5'),
    ]
    const foreground = cssVar('--foreground')
    const border = cssVar('--border')
    echarts.registerTheme(name, {
      color: palette,
      backgroundColor: 'transparent',
      textStyle: { color: foreground },
      title: { textStyle: { color: foreground } },
      legend: { textStyle: { color: foreground } },
      tooltip: {
        backgroundColor: cssVar('--popover'),
        borderColor: border,
        textStyle: { color: cssVar('--popover-foreground') },
      },
      categoryAxis: {
        axisLine: { lineStyle: { color: border } },
        axisLabel: { color: foreground },
        splitLine: { show: false },
      },
      valueAxis: {
        axisLine: { show: false },
        axisLabel: { color: foreground },
        splitLine: { lineStyle: { color: border } },
      },
    })
    document.documentElement.classList.toggle('dark', wasDark)
  }
}

/** ECharts sarmalayıcısı: temayı ve boyut değişimini yönetir (görev dosyası). */
export function EChart({
  option,
  className,
  height = 320,
  notMerge = true,
  onClick,
  width,
  renderer = 'canvas',
  light = false,
}: EChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<echarts.ECharts | null>(null)
  const theme = useThemeStore((state) => state.theme)
  const isDark = !light && resolveIsDark(theme)

  useEffect(() => {
    registerThemesOnce()
    const container = containerRef.current
    if (!container) return
    const chart = echarts.init(container, isDark ? DARK_THEME : LIGHT_THEME, { renderer })
    chartRef.current = chart
    // Dışarıdan (baskı hazır sinyali) okunur: ECharts çizimi bitirince `finished` olayı gelir.
    container.dataset.chartState = 'pending'
    const markFinished = () => {
      container.dataset.chartState = 'ready'
    }
    chart.on('finished', markFinished)

    const resizeObserver = new ResizeObserver(() => chart.resize())
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
      chart.dispose()
      chartRef.current = null
    }
    // Tema/çizici değişince örnek yeniden kurulur (ECharts bir kez init edilen temayı değiştirmez).
  }, [isDark, renderer])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    chart.getDom().dataset.chartState = 'pending'
    chart.setOption(option, { notMerge })
  }, [option, notMerge])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !onClick) return
    chart.on('click', onClick)
    return () => {
      chart.off('click', onClick)
    }
    // `isDark`: tema değişince örnek yeniden kurulur, dinleyici yenisine bağlanmalı.
  }, [onClick, isDark, renderer])

  return <div ref={containerRef} className={className} style={{ height, width }} />
}
